import { useCallback, useEffect, useState } from 'react'
import {
  fetchAutoExecuteStatus,
  fetchRedemptionStats,
  startAutoExecute,
  stopAutoExecute,
  startRedemptionWorker,
  stopRedemptionWorker,
  type AutoExecuteWorkerStatus,
  type RedemptionWorkerStats,
} from '../../lib/api.ts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

function StatusDot({ running }: { running: boolean }) {
  return (
    <span
      className={`live-dot ${running ? 'animate-pulse-dot' : ''}`}
      style={{ color: running ? 'var(--green)' : 'var(--text-muted)', fontSize: '8px' }}
    >
      ●
    </span>
  )
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never'
  const diff = Date.now() - new Date(dateStr).getTime()
  const s = Math.floor(diff / 1000)
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  return `${Math.floor(m / 60)}h ago`
}

interface AutoWorkerPanelProps {
  status: AutoExecuteWorkerStatus | null
  loading: boolean
  onStart: () => void
  onStop: () => void
}

function AutoWorkerPanel({ status, loading, onStart, onStop }: AutoWorkerPanelProps) {
  const running = status?.running ?? false
  const stats = status?.stats

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <StatusDot running={running} />
          <span>Auto-Execute Worker</span>
          <Badge variant={running ? 'default' : 'secondary'} className="ml-auto">
            {running ? 'RUNNING' : 'STOPPED'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {stats && (
          <div className="worker-stat-grid" style={{ marginBottom: '16px' }}>
            <div className="worker-stat">
              <div className="worker-stat-label">Scans</div>
              <div className="worker-stat-value">{stats.scansCompleted}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Trades</div>
              <div className="worker-stat-value">{stats.tradesExecuted}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Invested</div>
              <div className="worker-stat-value" style={{ fontSize: '13px' }}>${(stats.totalInvested ?? 0).toFixed(2)}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Errors</div>
              <div className="worker-stat-value" style={{ color: stats.errors > 0 ? 'var(--red)' : 'var(--text-primary)' }}>
                {stats.errors}
              </div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Successful Merges</div>
              <div className="worker-stat-value">{stats.hedgesFound}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Failed Attempts</div>
              <div className="worker-stat-value">{stats.entriesFound}</div>
            </div>
          </div>
        )}

        {stats?.lastError && (
          <div style={{ padding: '8px 10px', background: 'var(--red-muted)', border: '1px solid var(--red)', fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--red)', marginBottom: '12px', wordBreak: 'break-word' }}>
            {stats.lastError}
          </div>
        )}

        {stats && (
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: '12px' }}>
            Last scan: {formatTimeAgo(stats.lastScanAt)} · Last trade: {formatTimeAgo(stats.lastTradeAt)}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          {running ? (
            <Button variant="destructive" onClick={onStop} disabled={loading} type="button">
              ■ Stop Worker
            </Button>
          ) : (
            <Button variant="default" onClick={onStart} disabled={loading} type="button">
              ▶ Start Worker
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface RedemptionPanelProps {
  stats: RedemptionWorkerStats | null
  loading: boolean
  onStart: () => void
  onStop: () => void
}

function RedemptionPanel({ stats, loading, onStart, onStop }: RedemptionPanelProps) {
  const running = stats?.running ?? false

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <StatusDot running={running} />
          <span>Redemption Worker</span>
          <Badge variant={running ? 'default' : 'secondary'} className="ml-auto">
            {running ? 'RUNNING' : 'STOPPED'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {stats && (
          <div className="worker-stat-grid" style={{ marginBottom: '16px' }}>
            <div className="worker-stat">
              <div className="worker-stat-label">Scans</div>
              <div className="worker-stat-value">{stats.scansCompleted}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Redeemed</div>
              <div className="worker-stat-value">{stats.positionsRedeemed}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Received</div>
              <div className="worker-stat-value" style={{ fontSize: '13px' }}>${(stats.totalAmountReceived ?? 0).toFixed(2)}</div>
            </div>
            <div className="worker-stat">
              <div className="worker-stat-label">Errors</div>
              <div className="worker-stat-value" style={{ color: stats.errors > 0 ? 'var(--red)' : 'var(--text-primary)' }}>
                {stats.errors}
              </div>
            </div>
          </div>
        )}

        {stats?.lastError && (
          <div style={{ padding: '8px 10px', background: 'var(--red-muted)', border: '1px solid var(--red)', fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--red)', marginBottom: '12px', wordBreak: 'break-word' }}>
            {stats.lastError}
          </div>
        )}

        {stats && (
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: '12px' }}>
            Last scan: {formatTimeAgo(stats.lastScanAt)} · Last redemption: {formatTimeAgo(stats.lastRedemptionAt)}
          </div>
        )}

        <div style={{ display: 'flex', gap: '8px' }}>
          {running ? (
            <Button variant="destructive" onClick={onStop} disabled={loading} type="button">
              ■ Stop Worker
            </Button>
          ) : (
            <Button variant="default" onClick={onStart} disabled={loading} type="button">
              ▶ Start Worker
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function WorkerControls() {
  const [autoStatus, setAutoStatus] = useState<AutoExecuteWorkerStatus | null>(null)
  const [redemptionStats, setRedemptionStats] = useState<RedemptionWorkerStats | null>(null)
  const [autoLoading, setAutoLoading] = useState(false)
  const [redemptionLoading, setRedemptionLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [auto, redemption] = await Promise.all([
        fetchAutoExecuteStatus().catch(() => null),
        fetchRedemptionStats().catch(() => null),
      ])
      if (auto) setAutoStatus(auto)
      if (redemption?.success && redemption.stats) setRedemptionStats(redemption.stats)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load worker data')
    }
  }, [])

  useEffect(() => {
    load()
    const interval = setInterval(load, 3000)
    return () => clearInterval(interval)
  }, [load])

  const handleStartAuto = async () => {
    setAutoLoading(true)
    try {
      await startAutoExecute()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start worker')
    } finally {
      setAutoLoading(false)
    }
  }

  const handleStopAuto = async () => {
    setAutoLoading(true)
    try {
      await stopAutoExecute()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop worker')
    } finally {
      setAutoLoading(false)
    }
  }

  const handleStartRedemption = async () => {
    setRedemptionLoading(true)
    try {
      await startRedemptionWorker()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start redemption worker')
    } finally {
      setRedemptionLoading(false)
    }
  }

  const handleStopRedemption = async () => {
    setRedemptionLoading(true)
    try {
      await stopRedemptionWorker()
      await load()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop redemption worker')
    } finally {
      setRedemptionLoading(false)
    }
  }

  return (
    <div className="animate-fade-in-up">
      {error && (
        <div style={{ padding: '12px', color: 'var(--red)', fontFamily: 'var(--font-mono)', fontSize: '11px', background: 'var(--red-muted)', border: '1px solid var(--red)', marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: '11px' }} onClick={() => setError(null)} type="button">✕</button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        <AutoWorkerPanel
          status={autoStatus}
          loading={autoLoading}
          onStart={handleStartAuto}
          onStop={handleStopAuto}
        />
        <RedemptionPanel
          stats={redemptionStats}
          loading={redemptionLoading}
          onStart={handleStartRedemption}
          onStop={handleStopRedemption}
        />
      </div>
    </div>
  )
}